import { sanitizeUrl } from './core.js';

const MAXIMUM_RESOURCES = 100;

const INITIATOR_ALIASES = {
    img: 'img',
    image: 'img',
    script: 'script',
    link: 'css',
    css: 'css',
    cssom: 'css',
    fetch: 'fetch',
    xmlhttprequest: 'xmlhttprequest',
    beacon: 'beacon',
    iframe: 'iframe',
    video: 'media',
    audio: 'media',
    track: 'media',
    font: 'font',
    other: 'other',
};

export class ResourceMonitor {
    constructor(browser, { isCollectorUrl = () => false, maximumResources = MAXIMUM_RESOURCES } = {}) {
        this.browser = browser;
        this.isCollectorUrl = isCollectorUrl;
        this.maximumResources = Math.max(1, maximumResources);
        this.resources = [];
        this.seenKeys = new Set();
        this.observer = null;
        this.pageStartedAt = 0;
        this.installed = false;
    }

    install(pageStartedAt = Date.now()) {
        this.reset(pageStartedAt);

        if (this.installed) {
            return;
        }

        this.installed = true;
        this.observe();
    }

    reset(pageStartedAt = Date.now()) {
        this.disconnectObserver();
        this.resources = [];
        this.seenKeys = new Set();
        this.pageStartedAt = pageStartedAt;
        this.drainBufferedEntries();
        this.observe();
    }

    disconnect() {
        this.disconnectObserver();
        this.installed = false;
    }

    disconnectObserver() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
    }

    observe() {
        const PerformanceObserver = this.browser.PerformanceObserver;

        if (typeof PerformanceObserver !== 'function') {
            return;
        }

        try {
            this.observer = new PerformanceObserver((list) => {
                this.ingestEntries(list.getEntries?.() ?? []);
            });
            this.observer.observe({ type: 'resource', buffered: true });
        } catch {
            try {
                this.observer = new PerformanceObserver((list) => {
                    this.ingestEntries(list.getEntries?.() ?? []);
                });
                this.observer.observe({ entryTypes: ['resource'] });
            } catch {
                this.observer = null;
            }
        }
    }

    drainBufferedEntries() {
        const performance = this.browser.performance;

        if (! performance || typeof performance.getEntriesByType !== 'function') {
            return;
        }

        try {
            this.ingestEntries(performance.getEntriesByType('resource') ?? []);
        } catch {
            // Resource Timing unavailable in this environment.
        }
    }

    ingestEntries(entries) {
        for (const entry of entries) {
            this.addEntry(entry);
        }
    }

    addEntry(entry) {
        if (! entry || typeof entry.name !== 'string' || entry.name === '') {
            return;
        }

        const url = sanitizeUrl(entry.name, this.browser.location?.href ?? entry.name);

        if (! url || this.isCollectorUrl(url) || this.isTrackerAsset(url)) {
            return;
        }

        const startOffsetMs = this.boundedInteger(entry.startTime, 0, 86400000) ?? 0;
        const initiatorType = this.normalizeInitiator(entry.initiatorType);
        const key = `${url}|${startOffsetMs}|${initiatorType}`;

        if (this.seenKeys.has(key)) {
            return;
        }

        if (this.resources.length >= this.maximumResources) {
            return;
        }

        this.seenKeys.add(key);
        this.resources.push({
            url,
            initiator_type: initiatorType,
            duration_ms: this.boundedInteger(entry.duration, 0, 600000),
            transfer_size_bytes: this.boundedInteger(entry.transferSize, 0, 104857600),
            encoded_size_bytes: this.boundedInteger(entry.encodedBodySize, 0, 104857600),
            start_offset_ms: startOffsetMs,
        });
    }

    isTrackerAsset(url) {
        try {
            const pathname = new URL(url).pathname;

            return /\/tracker(?:-replay)?\.js$/i.test(pathname);
        } catch {
            return false;
        }
    }

    normalizeInitiator(value) {
        const normalized = String(value ?? 'other').toLowerCase();

        return INITIATOR_ALIASES[normalized] ?? 'other';
    }

    boundedInteger(value, minimum, maximum) {
        const number = Number(value);

        if (! Number.isFinite(number)) {
            return null;
        }

        return Math.max(minimum, Math.min(maximum, Math.round(number)));
    }

    snapshot() {
        return this.resources.map((resource) => ({ ...resource }));
    }

    count() {
        return this.resources.length;
    }
}
